import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { faGhost } from "@fortawesome/pro-duotone-svg-icons";
import {
  faAngleLeft,
  faAngleRight,
  faFilter,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Alert,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";

import { useBasePath, useOrg } from "@hooks";

import { fetchDemoProjectStats } from "@pages/overview/api";
import { ProjectAnalyticsItem, WidgetInterface } from "@pages/overview/types";

import { useProjectStats } from "../hooks/useProjectStats";

import { AddProjectsButton } from "./AddProjectsButton";

const PAGE_SIZE = 5;

const MinWidthNumberCell = styled.td`
  width: 1%;
  text-align: right;
`;

export const OverviewProjectsWidget: React.FC<WidgetInterface> = ({
  hasProjects,
  reposFilter,
}) => {
  const theme = useMantineTheme();
  const basePath = useBasePath();
  const [error, setError] = useState(false);
  const [demoProjectStats, setDemoProjectStats] =
    useState<ProjectAnalyticsItem[]>();
  const history = useHistory();
  const org = useOrg();
  const { data: loadedProjectStats } = useProjectStats(reposFilter);
  const [pageNum, setPageNum] = useState(0);
  const [filter, setFilter] = useState("");

  const projectStats = useMemo(() => {
    const unfiltered = hasProjects
      ? loadedProjectStats?.project_analytics_items
      : demoProjectStats;
    return unfiltered?.filter((item) =>
      item.project_name.toLowerCase().includes(filter.toLowerCase())
    );
  }, [loadedProjectStats, demoProjectStats, hasProjects, filter]);

  useEffect(() => {
    if (hasProjects || !org) return;
    fetchDemoProjectStats(org.id)
      .then((res) => setDemoProjectStats(res.project_analytics_items))
      .catch(() => setError(true));
  }, [hasProjects, org]);

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNum(0);
    setFilter(e.currentTarget.value);
  };

  return (
    <div data-testid="projects-card">
      <Group position="apart" px="xs" pb="xs">
        <TextInput
          placeholder="Filter..."
          icon={<FontAwesomeIcon icon={faFilter} />}
          value={filter}
          onChange={onFilterChange}
          disabled={projectStats === undefined || error}
        />
        <Group spacing="xs">
          <ActionIcon
            variant="outline"
            color="default"
            disabled={pageNum === 0 || projectStats === undefined || error}
            onClick={() => setPageNum(pageNum - 1)}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </ActionIcon>
          <ActionIcon
            variant="outline"
            color="default"
            disabled={
              projectStats === undefined ||
              error ||
              projectStats.length <= PAGE_SIZE * (pageNum + 1)
            }
            onClick={() => setPageNum(pageNum + 1)}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </ActionIcon>
        </Group>
      </Group>
      <Table highlightOnHover striped horizontalSpacing="lg">
        <thead>
          <tr>
            <th>Project name</th>
            <th style={{ whiteSpace: "nowrap" }}>Open findings</th>
            <th style={{ whiteSpace: "nowrap" }}>High severity</th>
            <th style={{ whiteSpace: "nowrap" }}>Fix rate</th>
          </tr>
        </thead>
        <tbody>
          {!error &&
            (projectStats === undefined
              ? [0, 1, 2].map((idx) => (
                  <tr key={idx}>
                    <td>
                      <Skeleton height={16} />
                    </td>
                    <MinWidthNumberCell>
                      <Skeleton height={16} width={24} ml="auto" />
                    </MinWidthNumberCell>
                    <MinWidthNumberCell>
                      <Skeleton height={16} width={24} ml="auto" />
                    </MinWidthNumberCell>
                    <MinWidthNumberCell>
                      <Skeleton height={16} width={24} ml="auto" />
                    </MinWidthNumberCell>
                  </tr>
                ))
              : projectStats
                  .slice(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE)
                  .map((p) => (
                    <tr
                      key={p.project_name}
                      onClick={() =>
                        history.push(
                          `${basePath}/findings?repo=${p.project_name}`
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td data-testid="project-name">{p.project_name}</td>
                      <MinWidthNumberCell data-testid="open-count">
                        {p.open_findings_count}
                      </MinWidthNumberCell>
                      <MinWidthNumberCell data-testid="severity-high-count">
                        {p.severity_high_count}
                      </MinWidthNumberCell>
                      <MinWidthNumberCell data-testid="fix-rate">{`${Math.round(
                        p.fixed_findings_rate * 100.0
                      )}%`}</MinWidthNumberCell>
                    </tr>
                  )))}
        </tbody>
      </Table>
      {!error && projectStats?.length === 0 && (
        <Stack
          p="lg"
          sx={{ color: theme.colors["gray"][6] }}
          align="center"
          justify="center"
        >
          <FontAwesomeIcon
            icon={faGhost}
            size="3x"
            color={theme.colors["blue"][3]}
          />
          <Text align="center" size="sm">
            <Title order={4} weight="normal">
              No projects found
            </Title>
            Try adjusting your filters
          </Text>
        </Stack>
      )}
      {error && <Text color="red.8">Failed to load data</Text>}
      {!hasProjects && projectStats !== undefined && (
        <Alert color="violet" mt="xs">
          <Group position="apart">
            <div>
              <b>Scan your code!</b>
              &nbsp; Scan locally or set up CI scans.
            </div>
            <AddProjectsButton />
          </Group>
        </Alert>
      )}
    </div>
  );
};
